import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { SectionHeading } from '@/components/landing/section-heading'
import { STORY_CHAPTERS } from '@/constants/landing'

export const StorySection: FC = (): ReactNode => {
    const [active, setActive] = useState(0)
    const chapterRefs = useRef<(HTMLLIElement | null)[]>([])

    // The chapter crossing the middle of the screen becomes the active one.
    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return

                const index = Number((entry.target as HTMLElement).dataset.index)
                if (!Number.isNaN(index)) setActive(index)
            })
        }, { rootMargin: '-45% 0px -45% 0px' })

        chapterRefs.current.forEach((element) => {
            if (element) observer.observe(element)
        })

        return () => observer.disconnect()
    }, [])

    const chapter = STORY_CHAPTERS[active] ?? STORY_CHAPTERS[0]
    const { scale, x, y } = chapter.focus

    return (
        <section id='house' aria-labelledby='house-title' className='relative isolate bg-sand/45 py-24 sm:py-32'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <SectionHeading
                    id='house-title'
                    eyebrow="The house"
                    title={<>A family atelier, <em className='text-gilded'>since 1908</em></>}
                    description="Four generations of the Aveline family have kept the rule our founder set on her first day: every piece is made from beginning to end by a single pair of hands."
                />

                <div className='mt-16 grid gap-10 lg:mt-20 lg:grid-cols-2 lg:gap-20'>
                    <div className='lg:sticky lg:top-28 lg:h-[calc(100svh-9rem)] lg:self-start'>
                        <div className='relative h-[24rem] overflow-hidden rounded-[2.5rem] bg-stone shadow-[0_40px_80px_-48px_rgba(34,28,22,0.6)] sm:h-[30rem] lg:h-full'>
                            <Image
                                src='/images/story/workshop.jpg'
                                alt="A craftsman's workbench in the Aveline atelier, laid with hand tools in soft morning light"
                                fill
                                sizes='(min-width: 1024px) 46vw, 92vw'
                                className='object-cover transition-transform duration-[1800ms] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none'
                                style={{ transform: `scale(${scale}) translate(${x}%, ${y}%)` }}
                            />

                            <div aria-hidden='true' className='pointer-events-none absolute inset-0 bg-linear-to-t from-espresso/30 via-transparent to-transparent' />

                            <p className='glass glass-clear absolute left-5 top-5 rounded-full px-4 py-2 text-[0.64rem] uppercase tracking-[0.24em] text-espresso'>
                                {chapter.place}
                            </p>

                            <div className='glass absolute inset-x-5 bottom-5 flex items-end justify-between gap-6 rounded-[1.75rem] p-5 text-espresso sm:p-6'>
                                <p key={chapter.year} className='font-heading text-6xl font-light leading-none animate-in fade-in slide-in-from-bottom-2 duration-700 sm:text-7xl'>
                                    {chapter.year}
                                </p>

                                <div className='flex items-center gap-1.5 pb-2' aria-hidden='true'>
                                    {STORY_CHAPTERS.map((item, index) => (
                                        <span
                                            key={item.year}
                                            className={cn(
                                                'h-1 rounded-full transition-all duration-500',
                                                index === active ? 'w-8 bg-espresso' : 'w-3 bg-espresso/25'
                                            )}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <ol>
                        {STORY_CHAPTERS.map((item, index) => (
                            <li
                                key={item.year}
                                ref={(element) => {
                                    chapterRefs.current[index] = element
                                }}
                                data-index={index}
                                className='flex flex-col justify-center border-t border-espresso/10 py-10 first:border-t-0 first:pt-0 lg:min-h-[72svh] lg:border-t-0 lg:py-0'
                            >
                                <div
                                    className={cn(
                                        'transition-opacity duration-700',
                                        index === active ? 'lg:opacity-100' : 'lg:opacity-35'
                                    )}
                                >
                                    <p className='flex items-center gap-4 text-[0.68rem] uppercase tracking-[0.28em] text-gold-deep'>
                                        <span className='font-heading text-3xl normal-case italic tracking-normal'>
                                            {item.year}
                                        </span>
                                        <span aria-hidden='true' className='h-px w-10 bg-gold-deep/40' />
                                        {item.place}
                                    </p>

                                    <h3 className='mt-5 font-heading text-4xl font-light leading-[1.05] text-espresso sm:text-5xl'>
                                        {item.title}
                                    </h3>

                                    <p className='mt-5 max-w-md text-base leading-relaxed text-muted-foreground'>
                                        {item.text}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ol>
                </div>
            </div>
        </section>
    )
}
